import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';

import { BaseComponent } from 'src/app/core/components/base/base.component';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { SuppliersService } from 'src/app/data/service/suppliers.service';
import { BankAccountService } from 'src/app/data/service/bank-account.service';
import { ToasterMessageService } from '../../toaster-message/toaster-message.service';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';

import { PaymentAccount } from 'src/app/modules/bookkeeping/models/account/payment-account';
import { SupplierShort } from 'src/app/modules/bookkeeping/models/supplier/supplier-short';
import { TaxRate } from 'src/app/modules/bookkeeping/models/taxRate';
import { TaxBasis } from 'src/app/modules/bookkeeping/enums/taxBasis';
import { QBOClass } from 'src/app/modules/bookkeeping/models/qbo-class';
import { CurrencyCode } from 'src/app/modules/bookkeeping/enums/currency-code';
import { AccountingIntegration } from 'src/app/data/enums/accounting-integration';
import { NewSupplier } from 'src/app/modules/bookkeeping/models/supplier/new-supplier';
import { SupplierWithAlias } from 'src/app/modules/bookkeeping/models/supplier/supplier-with-alias';
import * as $ from 'jquery';

export interface ManualCodingTransaction {
  id: string;
  date: string | Date;
  description: string;
  amount: number;
  accountName?: string;
}

export interface ManualCodingPayload {
  transactionIds: string[];
  shared: {
    supplierId: string;
    supplierName: string;
    accountId: string;
    accountName: string;
    details: string;
    currency: CurrencyCode;
    taxRateId: string;
    taxBasis: TaxBasis;
    classId: string | null;
    costCentreId: string | null;
    jobs: any[];
  };
}

/**
 * AI-suggested coding for a transaction (supplier + account). When passed to
 * the modal it pre-fills those fields and flags them as AI-suggested.
 */
export interface AiCodingSuggestion {
  supplierId: string;
  supplierName: string;
  accountId: string;
  accountName: string;
  confidence?: number;
  reason?: string;
  /**
   * SD-1254: how sure the supplier is. 'possible' pre-fills it to be checked; 'new' and 'none'
   * leave it empty, 'new' offering to create the supplier by the name the AI read. Absent: 'matched'.
   */
  supplierMatch?: 'matched' | 'possible' | 'new' | 'none';
  newSupplierName?: string;
}

@Component({
  selector: 'app-manual-coding-modal',
  templateUrl: './manual-coding-modal.component.html',
  styleUrls: ['./manual-coding-modal.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
})
export class ManualCodingModalComponent extends BaseComponent implements OnInit {
  /**
   * Transactions being coded. Pass one for single-coding, many for bulk.
   * The modal renders a context card (single) or a stacked summary (multi)
   * so the user always knows exactly what they're applying coding to.
   */
  @Input() transactions: ManualCodingTransaction[] = [];

  // ── Optional pre-loaded lookup data ─────────────────────────────────────
  // When the parent already has these (typical case — page-load prefetch),
  // pass them in as inputs and the modal will skip its own API calls.
  @Input() suppliers: SupplierShort[] | null = null;
  @Input() accounts: PaymentAccount[] | null = null;
  @Input() taxRates: TaxRate[] | null = null;
  @Input() qboClasses: QBOClass[] | null = null;
  @Input() defaultTaxRateId: string | null = null;
  @Input() defaultCurrency: CurrencyCode | null = null;
  @Input() orgTaxBasis: TaxBasis = TaxBasis.DefaultRate;
  @Input() isQuickBook: boolean | null = null;
  @Input() isNoAccounting: boolean | null = null;

  /**
   * Optional AI-suggested coding. When supplied, the modal pre-fills the
   * Supplier and Account fields and marks them as AI-suggested until the
   * user changes them.
   */
  @Input() aiSuggestion: AiCodingSuggestion | null = null;

  /**
   * Renders the form in place rather than as a dialog: the reconcile panel's "Code it yourself",
   * so coding from the panel is the same form as coding from a row.
   */
  @Input() inline = false;
  /** Inline only: a request is out, so the button says so and cannot be pressed again. */
  @Input() busy = false;

  @Output() submit = new EventEmitter<ManualCodingPayload>();
  @Output() close = new EventEmitter<void>();

  form: FormGroup;

  loadingSuppliers = false;
  loadingAccounts = false;
  loadingTaxRates = false;

  selectedSupplierId: string = null;
  selectedSupplierName: string = null;
  selectedAccountId: string = null;
  selectedAccountName: string = null;
  selectedTaxRateId: string = null;
  selectedQBOClassId: string = null;

  /** True while the field still holds the (untouched) AI-suggested value. */
  supplierFromAi = false;
  /** SD-1254: the pre-filled supplier is the AI's guess, to be checked. */
  supplierPossible = false;
  /** SD-1254: none of the organisation's suppliers is this one: the one to create, by the AI's name for it. */
  suggestedNewSupplier: string = null;
  accountFromAi = false;

  /** Limits how many txn references render before collapsing into "+ N more". */
  readonly maxVisibleRefs = 4;
  showAllRefs = false;

  isNewSupplierLoaded: boolean = false;
  isNewSupplier: boolean = false;
  newSupplier: NewSupplier = new NewSupplier(this.organizationService.selectedOrganization);
  creatingNewSupplier: boolean = false;
  isCreateSupplierItemShown: boolean = false;
  currentSupplier: SupplierWithAlias = new SupplierWithAlias();
  account: PaymentAccount = new PaymentAccount();

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService,
    private supplierService: SuppliersService,
    private bankAccountService: BankAccountService,
    private toaster: ToasterMessageService,
  ) {
    super();
    this.form = this.fb.group({
      supplier: [null, Validators.required],
      account: [null, Validators.required],
      taxRate: [null],
      qboClass: [null],
      details: [''],
    });
  }

  ngOnInit(): void {
    // Use what the parent provided; only fetch what's missing. Typical use
    // case: parent prefetches suppliers/accounts/tax rates/QBO classes on
    // page load → this modal opens instantly with no extra round-trips.
    if (this.isQuickBook === null || this.isNoAccounting === null) {
      this.detectAccountingIntegration();
    }
    if (!this.suppliers) {
      this.suppliers = [];
      this.loadSuppliers();
    }
    if (!this.accounts) {
      this.accounts = [];
      this.loadAccounts();
    }
    if (!this.taxRates) {
      this.taxRates = [];
      this.loadTaxRates();
    } else {
      this.applyDefaultTaxRate();
    }
    if (!this.defaultCurrency) {
      this.loadDefaultCurrency();
    }
    if (!this.qboClasses) {
      this.qboClasses = [];
      this.loadQBOClasses();
    }
    // Apply now for any lookup data the parent pre-supplied; the loaders above
    // re-run this once their async data arrives.
    this.applyAiSuggestion();
  }

  /**
   * Pre-fill the Supplier and Account fields from `aiSuggestion`. Idempotent —
   * called from ngOnInit and again after each lookup loader resolves, so it
   * applies as soon as the relevant list is available. If the suggested
   * supplier/account isn't in the list it's injected so ng-select can show it.
   */
  private applyAiSuggestion(): void {
    const ai = this.aiSuggestion;
    if (!ai) return;

    if (ai.supplierMatch === 'new' || ai.supplierMatch === 'none') {
      this.suggestedNewSupplier = ai.supplierMatch === 'new' ? (ai.newSupplierName || null) : null;
    } else if (!this.supplierFromAi && !this.loadingSuppliers && this.suppliers
      && (ai.supplierId || ai.supplierName)) {
      // GUIDs come back in mixed case — compare ids case-insensitively.
      const supId = (ai.supplierId || '').toLowerCase();
      const supName = (ai.supplierName || '').toLowerCase();
      let sup: any = this.suppliers.find((s: any) =>
        (supId && (s.id || '').toLowerCase() === supId) ||
        (supName && (s.name || '').toLowerCase() === supName));
      if (!sup) {
        sup = { id: ai.supplierId, name: ai.supplierName };
        this.suppliers = [sup, ...this.suppliers];
      }
      this.selectedSupplierId = sup.id;
      this.selectedSupplierName = sup.name;
      this.form.patchValue({ supplier: sup });
      this.supplierFromAi = true;
      this.supplierPossible = ai.supplierMatch === 'possible';
    }

    if (!this.accountFromAi && !this.loadingAccounts && this.accounts
      && (ai.accountId || ai.accountName)) {
      const accId = (ai.accountId || '').toLowerCase();
      const accName = (ai.accountName || '').toLowerCase();
      let acc: any = this.accounts.find((a: any) =>
        (accId && (a.id || '').toLowerCase() === accId) ||
        (accName && (a.name || '').toLowerCase() === accName));
      if (!acc) {
        acc = { id: ai.accountId, name: ai.accountName };
        this.accounts = [acc, ...this.accounts];
      }
      this.selectedAccountId = acc.id;
      this.selectedAccountName = acc.name;
      this.form.patchValue({ account: acc });
      this.accountFromAi = true;
    }
  }

  /**
   * Apply the org's default tax rate to the form. Used when the parent
   * pre-supplies the tax rate id and we don't need to call
   * `getOrganizationTaxRateId` again.
   */
  private applyDefaultTaxRate(): void {
    if (this.defaultTaxRateId) {
      this.selectedTaxRateId = this.defaultTaxRateId;
      this.form.patchValue({ taxRate: this.defaultTaxRateId });
    }
  }

  // ── Mode helpers ─────────────────────────────────────────────────────────
  get isMulti(): boolean { return (this.transactions?.length || 0) > 1; }
  get isSingle(): boolean { return (this.transactions?.length || 0) === 1; }
  get count(): number { return this.transactions?.length || 0; }

  get title(): string {
    return this.isMulti ? `Code ${this.count} transactions` : 'Code transaction';
  }

  get submitLabel(): string {
    return this.isMulti ? `Code ${this.count} transactions` : 'Code transaction';
  }

  get visibleTransactions(): ManualCodingTransaction[] {
    const txns = this.transactions || [];
    return this.showAllRefs ? txns : txns.slice(0, this.maxVisibleRefs);
  }

  get hiddenCount(): number {
    return Math.max((this.transactions?.length || 0) - this.maxVisibleRefs, 0);
  }

  toggleAllRefs(): void { this.showAllRefs = !this.showAllRefs; }

  // ── Data loaders ─────────────────────────────────────────────────────────
  private detectAccountingIntegration(): void {
    this.organizationService.organizationList
      .pipe(this.untilThis)
      .subscribe((data) => {
        const org = data?.find(
          (o: any) => o.organisationID === this.organizationService.selectedOrganization
        );
        this.isQuickBook = org?.accountingIntegration === AccountingIntegration.QBO;
        this.isNoAccounting = org?.accountingIntegration === AccountingIntegration.None;
      });
  }

  private loadSuppliers(): void {
    this.loadingSuppliers = true;
    this.supplierService
      .getSuppliers(this.organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (suppliers: SupplierShort[]) => {
          const reg = /<[^>]+>/gi;
          this.suppliers = (suppliers || [])
            .map(s => ({ ...s, name: (s.name || '').replace(reg, '') }))
            .sort((a, b) => a.name.localeCompare(b.name));
          this.loadingSuppliers = false;
          this.applyAiSuggestion();
        },
        () => { this.loadingSuppliers = false; }
      );
  }

  private loadAccounts(): void {
    this.loadingAccounts = true;
    this.bankAccountService
      .getPaymentAccounts(this.organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (accounts: PaymentAccount[]) => {
          this.accounts = (accounts || []).filter(a => (a as any).isActive !== false);
          this.loadingAccounts = false;
          this.applyAiSuggestion();
        },
        () => { this.loadingAccounts = false; }
      );
  }

  private loadTaxRates(): void {
    this.loadingTaxRates = true;
    this.organizationService
      .getTaxRates(this.organizationService.selectedOrganization)
      .pipe(this.untilThis)
      .subscribe(
        (rates: TaxRate[]) => {
          this.taxRates = rates || [];
          this.loadingTaxRates = false;
          this.loadOrgTaxRate();
        },
        (e: Error) => {
          this.loadingTaxRates = false;
          this.toaster.displayToasterState(ToasterTypes.Error, 'Error', e.message);
        }
      );
  }

  private loadOrgTaxRate(): void {
    this.organizationService
      .getOrganizationTaxRateId(this.organizationService.selectedOrganization)
      .pipe(this.untilThis)
      .subscribe((response: any) => {
        if ((this.isQuickBook || this.isNoAccounting) && response?.taxBasis === TaxBasis.IgnoreTax) {
          this.orgTaxBasis = response.taxBasis;
          this.selectedTaxRateId = '0';
        } else {
          this.selectedTaxRateId = response?.taxRateId;
          this.form.patchValue({ taxRate: this.selectedTaxRateId });
        }
      });
  }

  private loadDefaultCurrency(): void {
    this.organizationService
      .getOrganizationDefaultCurrency(this.organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe(
        (response: any) => { this.defaultCurrency = response?.currency; },
        () => { /* non-blocking */ }
      );
  }

  private loadQBOClasses(): void {
    const orgId = this.organizationService.selectedOrganization;
    if (!orgId) return;
    this.organizationService
      .getOrganizationQBOClasses(orgId)
      .pipe(this.untilThis)
      .subscribe(
        (classes: QBOClass[]) => { this.qboClasses = classes || []; },
        () => { this.qboClasses = []; }
      );
  }

  // ── Field handlers ───────────────────────────────────────────────────────
  onSelectSupplier(event: SupplierShort): void {
    this.selectedSupplierId = event?.id ?? null;
    this.selectedSupplierName = event?.name ?? null;
    this.supplierFromAi = false;
    this.supplierPossible = false;
  }

  handleSupplierName(event) {
    this.newSupplier.name = event;
    const matchedSupplier = this.suppliers?.filter(
      (s) => s.name == this.newSupplier.name
    );

    if (matchedSupplier?.length == 0 && event) {
      this.isCreateSupplierItemShown = true;
    } else if (!event) {
      this.handleSupplierBlur();
    } else {
      this.isCreateSupplierItemShown = false;
    }
  }

  handleSupplierBlur() {
    this.isCreateSupplierItemShown = this.isNewSupplier;
  }

  /** SD-1254: "+ Create" on the supplier the Coding Assistant read and the books do not have. */
  createSuggestedSupplier(): void {
    if (!this.suggestedNewSupplier || this.creatingNewSupplier) { return; }
    this.newSupplier.name = this.suggestedNewSupplier;
    this.createNewSupplier();
  }

  createNewSupplier() {
    if (!this.creatingNewSupplier) {
      this.creatingNewSupplier = true;
      this.addNewSupplier(this.newSupplier, false);
    }
  }

  addNewSupplier(
    supplier: NewSupplier,
    setInvoiceSupplierName: boolean = true
  ) {
    supplier.bankAccountRequestModel = null;
    supplier.name = setInvoiceSupplierName
      ? this.form.value.supplier
      : supplier.name;
    const name = JSON.parse(JSON.stringify(supplier.name));
    const newSupplier = JSON.parse(JSON.stringify(supplier));
    newSupplier.bsb = newSupplier.bsb?.replace('-', '');
    newSupplier.currency = this.defaultCurrency;
    newSupplier.IsSupplier = true;
    newSupplier.OrganizationId = this.organizationService.selectedOrganization;
    newSupplier.hexColorClass = 'sup_img_box_1';
    if (!newSupplier.defaultAccount.id) newSupplier.defaultAccount = null;
    this.creatingNewSupplier = true;
    this.supplierService
      .createSupplier(newSupplier)
      .pipe(this.untilThis)
      .subscribe((result) => {
        if (result?.supplierId == 'ConnectionLost') {
          if (this.isQuickBook) {
            this.organizationService.getIntegrationCredentials('qbo').subscribe((res) => {
              if (res) {
                this.organizationService.refreshConnectionURL = res.revokeEndPoint;
                this.toaster.displayToasterState(
                  ToasterTypes.ErrorWithHtml,
                  null,
                  `QuickBooks connection lost. Please <a  class=\"toastr-link\" href=\"${this.organizationService.refreshConnectionURL}\">refresh</a> connection`
                );
              }
            });
          } else {
            this.organizationService.getIntegrationCredentials('xero').subscribe((res) => {
              if (res) {
                this.organizationService.refreshConnectionURL = res.revokeEndPoint;
                this.toaster.displayToasterState(
                  ToasterTypes.ErrorWithHtml,
                  null,
                  `Xero connection lost. Please <a  class=\"toastr-link\" href=\"${this.organizationService.refreshConnectionURL}\">refresh</a> connection`
                );
              }
            });
          }
        } else if (result?.message !== undefined) {
          this.toaster.displayToasterState(
            ToasterTypes.Error,
            null,
            result?.message
          );
          this.isNewSupplier = false;
          this.creatingNewSupplier = false;
          this.isCreateSupplierItemShown = false;
          document.getElementById('refField')?.focus();
        } else {
          this.suppliers = [
            ...this.suppliers,
            {
              id: result.supplierId,
              name: supplier.name,
              defaultAccountCode: supplier?.defaultAccount.code,
            },
          ];
          this.isNewSupplier = false;
          this.creatingNewSupplier = false;
          this.isCreateSupplierItemShown = false;
          // SD-1254: the supplier just created is the one being coded to.
          this.selectedSupplierId = result.supplierId;
          this.selectedSupplierName = supplier.name;
          this.form.patchValue({ supplier: this.suppliers[this.suppliers.length - 1] });
          this.suggestedNewSupplier = null;
          document.getElementById('refField')?.focus();
          this.account = supplier?.defaultAccount;
          if (supplier?.defaultAccount?.name == '') {
            supplier.defaultAccount.name = null;
          }
          $('.ng-dropdown-panel').remove();
          $('#ddlsupplier').removeClass('ng-select-opened');
        }
      });
  }

  onSelectAccount(event: PaymentAccount): void {
    this.selectedAccountId = event?.id ?? null;
    this.selectedAccountName = event?.name ?? null;
    this.accountFromAi = false;
  }

  onSelectTaxRate(event: TaxRate): void {
    this.selectedTaxRateId = event?.id ?? null;
  }

  onSelectQBOClass(event: QBOClass): void {
    this.selectedQBOClassId = event?.id ?? null;
  }

  // ── Actions ──────────────────────────────────────────────────────────────
  onClose(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault?.();
    }
    this.close.emit();
  }

  onBackdropClick(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.form.invalid || !this.transactions?.length) {
      this.form.markAllAsTouched();
      return;
    }
    const payload: ManualCodingPayload = {
      transactionIds: this.transactions.map(t => t.id),
      shared: {
        supplierId: this.selectedSupplierId,
        supplierName: this.selectedSupplierName,
        accountId: this.selectedAccountId,
        accountName: this.selectedAccountName,
        details: this.form.value.details || '',
        currency: this.defaultCurrency,
        taxRateId: this.form.value.taxRate ?? this.selectedTaxRateId,
        taxBasis: this.orgTaxBasis,
        classId: this.form.value.qboClass || null,
        costCentreId: null,
        jobs: [],
      },
    };
    this.submit.emit(payload);
  }
}
